<script setup lang="ts">
import { EmptyState, TraceIdAlert } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import { customerOption, listCustomerOptions, type Customer } from '../api/customers'
import { exportReceivables, listReceivables, pageTotals, type ReceivableRow } from '../api/reports'
import { csvName, saveBlob } from '../utils/download'

const rows = ref<ReceivableRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')
const exporting = ref(false)

const customers = ref<Customer[]>([])
const refError = ref<unknown>(null)

// 参照下拉用 undefined 表示「未选」：0 会被 el-select 当成值显示成「0」，placeholder 就没了
const query = reactive({
  keyword: '',
  customer: undefined as number | undefined,
  period: '',
  page: 1,
  size: 20,
})

/** 本页合计：报表是聚合结果，页脚合计让人一眼看出规模 */
const totals = computed(() => pageTotals(rows.value))

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listReceivables({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      customer: query.customer,
      period: query.period || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

async function loadRefs(): Promise<void> {
  refError.value = null
  try {
    customers.value = await listCustomerOptions()
  } catch (e) {
    refError.value = e
  }
}

onMounted(() => {
  void load()
  void loadRefs()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

async function doExport(): Promise<void> {
  exporting.value = true
  error.value = null
  try {
    saveBlob(
      await exportReceivables({
        keyword: query.keyword.trim() || undefined,
        customer: query.customer,
        period: query.period || undefined,
      }),
      csvName('receivables'),
    )
    notice.value = 'CSV 已开始下载'
  } catch (e) {
    error.value = e
  } finally {
    exporting.value = false
  }
}
</script>

<template>
  <div class="yc-rep">
    <header class="yc-rep__bar">
      <el-input
        v-model="query.keyword"
        class="yc-rep__keyword"
        placeholder="客户编码 / 名称"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select
        v-model="query.customer"
        class="yc-rep__ref"
        placeholder="全部客户"
        clearable
        filterable
        @change="search"
      >
        <el-option v-for="c in customers" :key="c.id" :label="customerOption(c)" :value="c.id" />
      </el-select>
      <el-date-picker
        v-model="query.period"
        class="yc-rep__period"
        type="month"
        value-format="YYYY-MM"
        placeholder="全部账期"
        clearable
        @change="search"
      />
      <el-button @click="search">查询</el-button>
      <el-button @click="load()">刷新</el-button>
      <el-button v-perm="'ops:report:export'" :loading="exporting" @click="doExport">导出 CSV</el-button>
      <span v-if="notice" class="yc-rep__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-rep__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-rep__card">
      <el-table v-if="rows.length" :data="rows" row-key="customer_id">
        <el-table-column prop="customer_code" label="客户编码" width="130" />
        <el-table-column prop="customer_name" label="客户名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="period" label="账期" width="100" />
        <el-table-column prop="charge_count" label="计费笔数" width="100" align="right" />
        <el-table-column label="待结算金额" width="130" align="right">
          <template #default="{ row }">{{ Number(row.pending_amount).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="已结算金额" width="130" align="right">
          <template #default="{ row }">{{ Number(row.settled_amount).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="应收合计" width="130" align="right">
          <template #default="{ row }">{{ Number(row.total_amount).toFixed(2) }}</template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="暂无应收数据"
        description="订单签收并计费后，这里会按客户 + 账期汇总应收。"
      />
    </el-card>

    <p v-if="rows.length" class="yc-rep__totals">
      本页合计：计费 {{ totals.charge_count }} 笔，待结算 {{ totals.pending_amount.toFixed(2) }}，已结算
      {{ totals.settled_amount.toFixed(2) }}，应收 {{ totals.total_amount.toFixed(2) }}（共 {{ total }} 行）
    </p>

    <el-pagination
      v-if="total > query.size"
      class="yc-rep__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />
  </div>
</template>

<style scoped lang="scss">
.yc-rep {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 190px;
  }

  &__ref {
    width: 190px;
  }

  &__period {
    width: 150px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__card {
    margin-bottom: var(--yc-space-4);
  }

  &__totals {
    margin: var(--yc-space-3) 0 0;
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
